import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ToastKind = 'error' | 'info' | 'success';

export type ToastItem = {
  id: string;
  kind: ToastKind;
  message: string;
};

type ToastContextValue = {
  toasts: ToastItem[];
  showToast: (message: string, opts?: { kind?: ToastKind; timeoutMs?: number }) => void;
  dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider />');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, opts?: { kind?: ToastKind; timeoutMs?: number }) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const kind: ToastKind = opts?.kind ?? 'info';
      const timeoutMs = opts?.timeoutMs ?? 5000;

      setToasts((prev) => [...prev, { id, kind, message }]);

      window.setTimeout(() => {
        dismissToast(id);
      }, timeoutMs);
    },
    [dismissToast],
  );

  const value = useMemo(() => ({ toasts, showToast, dismissToast }), [toasts, showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
}

export function ToastViewport() {
  const { toasts, dismissToast } = useToast();

  return (
    <div
      aria-label="Toast notifications"
      className="fixed right-4 top-4 z-[1000] flex w-[min(420px,calc(100vw-2rem))] flex-col gap-2"
    >
      {toasts.map((t) => {
        const tone =
          t.kind === 'error'
            ? 'border-red-200 bg-red-50 text-red-800'
            : t.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-gray-200 bg-white text-gray-800';

        return (
          <div
            key={t.id}
            className={`rounded-md border px-3 py-2 shadow ${tone}`}
            role="status"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm">{t.message}</div>
              <button
                type="button"
                className="text-xs opacity-70 hover:opacity-100"
                onClick={() => dismissToast(t.id)}
              >
                Dismiss
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
